/**
 * Чистая логика мастера «Перегенерировать визуалы».
 *
 * Здесь всё, что можно проверить без DOM: раскладка товаров по матрице,
 * правило «выбор товара тянет всю категорию», предвыбор кабинетов и чтение
 * прогресса фоновой задачи. Компонент остаётся разметкой.
 */

import type {
  RegenerationPlan,
  RegenerationPlanCabinet,
  RegenerationPlanCategory,
} from '../../pipeline/api'

/** Строка шага «Товары»: один будущий или существующий товар макета. */
export interface RegenerationProductRow {
  key: string
  categoryId: number
  categoryPrefix: string
  categoryDescription: string
  /** Артикул, если товар уже создан; иначе его создадут при запуске. */
  article: string | null
  quantityIndex: number
  quantityTotal: number
  /** Категория рендерится одной задачей на все свои товары. */
  rendersWholeCategory: boolean
}

export function productRows(categories: RegenerationPlanCategory[]): RegenerationProductRow[] {
  return categories.flatMap((category) =>
    Array.from({ length: Math.max(1, category.quantity) }, (_unused, index) => ({
      key: `${category.category_id}:${index + 1}`,
      categoryId: category.category_id,
      categoryPrefix: category.category_prefix,
      categoryDescription: category.category_description,
      article: category.articles[index] ?? null,
      quantityIndex: index + 1,
      quantityTotal: Math.max(1, category.quantity),
      rendersWholeCategory:
        category.required_generation_mode === 'multi' || category.quantity > 1,
    })),
  )
}

/**
 * Отметка товара — это отметка его категории.
 *
 * Визуалы многотоварной категории (PP2/PL3) движок рендерит одной задачей на
 * все её артикулы, и запланировать цель половине из них нельзя: соседний товар
 * получил бы CONTENT_FUNNEL_PIN_CONFLICT. Поэтому единица выбора — категория,
 * и мастер обязан показывать это честно, а не обещать половину.
 */
export function toggleCategory(selected: number[], categoryId: number): number[] {
  return selected.includes(categoryId)
    ? selected.filter((value) => value !== categoryId)
    : [...selected, categoryId]
}

export const cabinetKey = (cabinet: { marketplace: string; seller_id: number }): string =>
  `${cabinet.marketplace}:${cabinet.seller_id}`

/**
 * Предвыбор кабинетов — те, что уже участвовали в прогонах макета.
 *
 * Это ровно тот набор, который берёт кнопка «Перегенерировать визуалы» без
 * мастера: открыть мастер и нажать «Далее» трижды должно давать прежний
 * результат. Если прогонов не было, не отмечаем ничего — выбор за человеком.
 */
export function initialCabinets(cabinets: RegenerationPlanCabinet[]): string[] {
  return cabinets.filter((cabinet) => cabinet.used_before).map(cabinetKey)
}

export function selectedTargets(
  cabinets: RegenerationPlanCabinet[],
  keys: string[],
): Array<{ marketplace: string; seller_id: number; cabinet: string }> {
  return cabinets
    .filter((cabinet) => keys.includes(cabinetKey(cabinet)))
    .map((cabinet) => ({
      marketplace: cabinet.marketplace,
      seller_id: cabinet.seller_id,
      cabinet: cabinet.cabinet,
    }))
}

/** Сколько товаров уйдёт в прогон и сколько из них будет создано. */
export function selectionSummary(
  plan: RegenerationPlan | null,
  selectedCategories: number[],
): { products: number; created: number; categories: number } {
  const categories = (plan?.categories || []).filter((category) =>
    selectedCategories.includes(category.category_id),
  )
  return {
    categories: categories.length,
    products: categories.reduce((sum, category) => sum + Math.max(1, category.quantity), 0),
    created: categories.reduce((sum, category) => sum + Math.max(0, category.missing), 0),
  }
}

/** Карточка прогресса фоновой задачи — в том же виде, что у мастера макетов. */
export interface RegenerationStageCard {
  key: string
  label: string
  current: number
  total: number
  determinate: boolean
  failed: number
  skipped: number
  state: 'idle' | 'running' | 'done' | 'failed'
}

const STAGE_STATE: Record<string, RegenerationStageCard['state']> = {
  completed: 'done',
  processing: 'running',
  failed: 'failed',
  pending: 'idle',
}

/**
 * Шаги считает бэкенд (агрегатор родительской задачи) и кладёт в метаданные.
 * Фронт их только показывает: вторая арифметика прогресса разошлась бы с первой.
 */
export function executionStages(job: any): RegenerationStageCard[] {
  const stages = Array.isArray(job?.metadata?.pipeline_stages)
    ? job.metadata.pipeline_stages
    : []
  return stages.map((stage: any) => ({
    key: String(stage.key || stage.label || ''),
    label: String(stage.label || 'Этап'),
    current: Number(stage.current || 0),
    total: Number(stage.total || 0),
    determinate: stage.determinate !== false,
    failed: Number(stage.failed || 0),
    skipped: Number(stage.skipped || 0),
    state: STAGE_STATE[String(stage.status)] || 'idle',
  }))
}

export const isExecutionFinished = (job: any): boolean =>
  ['completed', 'failed', 'cancelled'].includes(String(job?.status || ''))

export const isExecutionFailed = (job: any): boolean =>
  ['failed', 'cancelled'].includes(String(job?.status || ''))

export function executionHeadline(job: any): { title: string; description: string } {
  if (!job) {
    return {
      title: 'Запускаем перегенерацию',
      description: 'Создаём прогон конвейера…',
    }
  }
  if (isExecutionFailed(job)) {
    return {
      title: 'Перегенерация завершилась с ошибкой',
      description: String(job.error || 'Подробности — в журнале макета и фоновых задачах'),
    }
  }
  if (isExecutionFinished(job)) {
    return {
      title: 'Визуалы перегенерированы',
      description: 'Проверьте их на вкладке «Карточки МП»',
    }
  }
  return {
    title: 'Идёт перегенерация',
    description: String(job.currentStep || job.current_step || 'Ожидаем обновление состояния…'),
  }
}
